import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformApi, platformError } from './platformApi.js';
import { toast } from '../../shared/useToast.js';

const LIST_KEY = ['platform', 'companies'];

const EMPTY_STATS = { total: 0, pending: 0, active: 0, suspended: 0, rejected: 0 };

export function useCompanies({ status = '', q = '' } = {}) {
  return useQuery({
    queryKey: [...LIST_KEY, 'list', { status, q }],
    queryFn: async () => {
      const params = {};
      if (status) params.status = status;
      if (q) params.q = q;
      const { data } = await platformApi.get('/platform/companies', { params });
      return {
        companies: Array.isArray(data?.companies) ? data.companies : [],
        stats: { ...EMPTY_STATS, ...(data?.stats || {}) }
      };
    },
    staleTime: 10_000,
    placeholderData: (prev) => prev
  });
}

export function useCompany(id) {
  return useQuery({
    queryKey: [...LIST_KEY, 'one', String(id || '')],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await platformApi.get(`/platform/companies/${id}`);
      return { company: data?.company || null, usage: data?.usage || null };
    },
    staleTime: 5_000
  });
}

// mutation موحّد: toast للخطأ برسالة عربية + إبطال كاش القائمة بعد النجاح
function usePlatformMutation({ mutationFn, successMessage, onSuccess, onError }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (data, vars, ctx) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      if (successMessage) toast.success(successMessage);
      if (onSuccess) onSuccess(data, vars, ctx);
    },
    onError: (err, vars, ctx) => {
      toast.error(platformError(err));
      if (onError) onError(err, vars, ctx);
    }
  });
}

export function useCreateCompany(opts = {}) {
  return usePlatformMutation({
    mutationFn: async (body) => {
      const { data } = await platformApi.post('/platform/companies', body);
      return { company: data?.company || null, credentials: data?.credentials || null };
    },
    successMessage: 'تم إنشاء الشركة',
    ...opts
  });
}

export function useUpdateCompany(opts = {}) {
  return usePlatformMutation({
    mutationFn: async ({ id, body }) => {
      const { data } = await platformApi.patch(`/platform/companies/${id}`, body);
      return data?.company || null;
    },
    successMessage: 'تم حفظ التعديلات',
    ...opts
  });
}

export function useApproveCompany(opts = {}) {
  return usePlatformMutation({
    mutationFn: async (id) => {
      const { data } = await platformApi.post(`/platform/companies/${id}/approve`);
      return { company: data?.company || null, credentials: data?.credentials || null };
    },
    successMessage: 'تمت الموافقة وتفعيل الشركة',
    ...opts
  });
}

export function useRejectCompany(opts = {}) {
  return usePlatformMutation({
    mutationFn: async ({ id, reason }) => {
      const { data } = await platformApi.post(`/platform/companies/${id}/reject`, { reason });
      return data?.company || null;
    },
    successMessage: 'تم رفض الطلب',
    ...opts
  });
}

export function useSuspendCompany(opts = {}) {
  return usePlatformMutation({
    mutationFn: async ({ id, reason }) => {
      const { data } = await platformApi.post(`/platform/companies/${id}/suspend`, { reason });
      return data?.company || null;
    },
    successMessage: 'تم إيقاف الشركة',
    ...opts
  });
}

export function useResumeCompany(opts = {}) {
  return usePlatformMutation({
    mutationFn: async (id) => {
      const { data } = await platformApi.post(`/platform/companies/${id}/resume`);
      return data?.company || null;
    },
    successMessage: 'تم استئناف عمل الشركة',
    ...opts
  });
}

export function useResetOwnerPassword(opts = {}) {
  return usePlatformMutation({
    mutationFn: async (id) => {
      const { data } = await platformApi.post(`/platform/companies/${id}/reset-owner-password`);
      return { credentials: data?.credentials || null };
    },
    successMessage: 'تم إنشاء كلمة مرور جديدة للمالك',
    ...opts
  });
}

export function useDeleteCompany(opts = {}) {
  return usePlatformMutation({
    mutationFn: async ({ id, dropData }) => {
      const { data } = await platformApi.delete(`/platform/companies/${id}`, {
        params: { dropData: dropData ? 'true' : 'false' }
      });
      return { ok: data?.ok !== false, droppedData: !!data?.droppedData };
    },
    successMessage: 'تم حذف الشركة',
    ...opts
  });
}
